import React, { useState, useEffect } from 'react';
import './DormitoryLayout.css';
import { FaBed, FaMale, FaFemale, FaUsers } from 'react-icons/fa';
import { getUnavailableBeds } from '../utils/bedAvailability';
import { getAllBookingsFromFirestore } from '../utils/firestoreBooking';

const DormitoryLayout = ({ bedsToSelect, onConfirm, onCancel, selectedBeds: selectedBedsProp = [], unavailableBeds = [], checkInDate, checkOutDate, onDateChange, packageInfo, excludeBookingId }) => {
  const [selectedBeds, setSelectedBeds] = useState(selectedBedsProp);
  // Track upper/lower toggle for each room
  const [roomToggles, setRoomToggles] = useState({ A: 'lower', B: 'lower' });
  const [realTimeUnavailableBeds, setRealTimeUnavailableBeds] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleToggle = (roomId, position) => {
    setRoomToggles(prev => ({ ...prev, [roomId]: position }));
  }
  // Fetch real-time bed availability when dates change
  useEffect(() => {
    const fetchUnavailableBeds = async () => {
      if (checkInDate && checkOutDate) {
        setLoading(true);
        try {
          let unavailable = await getUnavailableBeds(checkInDate, checkOutDate);

          // If editing an existing booking, exclude its beds from being marked unavailable
          if (excludeBookingId) {
            try {
              const allBookings = await getAllBookingsFromFirestore();
              const thisBooking = allBookings.find(b => b.id === excludeBookingId);
              if (thisBooking && Array.isArray(thisBooking.selectedBeds)) {
                const thisBeds = thisBooking.selectedBeds.map(b => typeof b === 'object' ? (b.id || b.number || b.display) : b);
                unavailable = unavailable.filter(id => !thisBeds.includes(id));
              }
            } catch (e) {
              console.warn('DormitoryLayout: Failed to exclude current booking beds from availability:', e);
            }
          }

          setRealTimeUnavailableBeds(unavailable);
        } catch (error) {
          console.error('Error fetching bed availability:', error);
          setRealTimeUnavailableBeds([]);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchUnavailableBeds();
  }, [checkInDate, checkOutDate, excludeBookingId]);

  useEffect(() => {
    // Accept either array of IDs or array of objects with .id
    if (Array.isArray(selectedBedsProp) && selectedBedsProp.length > 0 && typeof selectedBedsProp[0] === "object") {
      setSelectedBeds(selectedBedsProp.map(b => b.id));
    } else {
      setSelectedBeds(selectedBedsProp);
    }
  }, [selectedBedsProp]);
  
  // Generate actual bed options for Room A and Room B
  const generateBedOptions = () => {
    const beds = [];
    
    // Combine real-time unavailable beds with any passed unavailable beds
    let allUnavailableBeds = [...realTimeUnavailableBeds, ...unavailableBeds];

    // Normalize currently selected beds (from prop) to IDs and exclude them from unavailable list
    const currentSelectedIds = Array.isArray(selectedBedsProp)
      ? (typeof selectedBedsProp[0] === 'object'
          ? selectedBedsProp.map(b => b.id)
          : selectedBedsProp)
      : [];
    const filteredUnavailable = allUnavailableBeds.filter(id => !currentSelectedIds.includes(id));
    
    // Room A: 22 beds (A1-A22), odd numbers are lower beds, even numbers are upper beds
    for (let i = 1; i <= 22; i++) {
      beds.push({
        id: `A${i}`,
        roomId: 'A',
        number: i,
        display: `A${i}`,
        type: 'male',
        position: i % 2 === 1 ? 'lower' : 'upper',
        available: !filteredUnavailable.includes(`A${i}`) // Exclude current booking's beds from unavailable
      });
    }
    
    // Room B: 16 beds (B1-B16), usually for females or families
    for (let i = 1; i <= 16; i++) {
      beds.push({
        id: `B${i}`,
        roomId: 'B',
        number: i,
        display: `B${i}`,
        type: 'female',
        position: i % 2 === 1 ? 'lower' : 'upper',
        available: !filteredUnavailable.includes(`B${i}`) // Exclude current booking's beds from unavailable
      });
    }
    
    return beds;
  };
  
  const bedOptions = generateBedOptions();
  
  const toggleBedSelection = (bedId) => {
    if (selectedBeds.includes(bedId)) {
      setSelectedBeds(selectedBeds.filter(id => id !== bedId));
    } else if (selectedBeds.length < bedsToSelect) {
      setSelectedBeds([...selectedBeds, bedId]);
    }
  };

  // Admin action: Select any beds (ignoring availability) up to the required count
  const selectAllBeds = () => {
    const allIds = bedOptions.map(b => b.id);
    const newSelection = allIds.slice(0, bedsToSelect);
    setSelectedBeds(newSelection);
  };
  
  const getBedTypeIcon = (type) => {
    switch(type) {
      case 'male': return <FaMale />;
      case 'female': return <FaFemale />;
      case 'mixed': return <FaUsers />;
      default: return <FaBed />;
    }
  };
  
  const getBedTypeLabel = (roomId) => {
    switch(roomId) {
      case 'A': return 'Room A (Mixed Dormitory)';
      case 'B': return 'Room B (Female/Family Dormitory)';
      default: return 'General';
    }
  };
  
  const handleConfirm = () => {
    // Map selected bed IDs to bed objects with display info
    const selectedBedObjects = selectedBeds.map(id => {
      const bed = bedOptions.find(b => b.id === id);
      return {
        id: bed.id,
        display: `${bed.id} (${bed.position} bed)`,
        type: bed.type,
        roomId: bed.roomId,
        position: bed.position
      };
    });
    
    onConfirm(selectedBedObjects);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  const isBedSelected = (bedId) => selectedBeds.includes(bedId);
  
  // Helper function to format date for input field
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    
    // If it's already in YYYY-MM-DD format, return as is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }
    
    // Parse display format (e.g., "Aug 15, 2023") and convert to YYYY-MM-DD
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    
    // Get local date components to avoid timezone issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Get today's date in YYYY-MM-DD format for min date
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  return (
    <div className="dormitory-layout">
      <div className="dormitory-header">
        <h2>Select Your Beds</h2>
        <p>Choose {bedsToSelect} bed{bedsToSelect > 1 ? 's' : ''} for your stay</p>
        
        {/* Date Selection Section */}
        <div className="date-selection-section">
          <h3>Select Your Stay Dates</h3>
          <div className="date-inputs">
            <div className="date-input-group">
              <label htmlFor="checkIn">Check-in Date</label>
              <input
                type="date"
                id="checkIn"
                value={formatDateForInput(checkInDate)}
                min={getTodayDate()}
                onChange={(e) => onDateChange && onDateChange('start', e.target.value)}
                required
              />
            </div>
            <div className="date-input-group">
              <label htmlFor="checkOut">Check-out Date</label>
              <input
                type="date"
                id="checkOut"
                value={formatDateForInput(checkOutDate)}
                min={formatDateForInput(checkInDate) || getTodayDate()}
                onChange={(e) => onDateChange && onDateChange('end', e.target.value)}
                disabled={packageInfo?.plan === 'Hourly Plan' || packageInfo?.plan === 'Weekly Plan' || packageInfo?.plan === 'Monthly Plan'}
                required
              />
            </div>
          </div>
        </div>
        
        {loading && (
          <div className="loading-indicator">
            <span>Loading bed availability...</span>
          </div>
        )}
      </div>
      
      <div className="dormitory-map">
        {/* Room A Section */}
        <div className="dormitory-section">
          <h3>{getBedTypeLabel('A')}</h3>
          <div className="room-info">
            <span className="bed-count">22 beds total</span>
            <span className="bed-type">Mixed Dormitory</span>
          </div>
          {/* Toggle for Room A */}
          <div className="bed-toggle-group">
            <button
              className={`bed-toggle-btn${roomToggles.A === 'lower' ? ' active' : ''}`}
              onClick={() => handleToggle('A', 'lower')}
            >
              Lower Beds
            </button>
            <button
              className={`bed-toggle-btn${roomToggles.A === 'upper' ? ' active' : ''}`}
              onClick={() => handleToggle('A', 'upper')}
            >
              Upper Beds
            </button>
          </div>
          <div className="bed-grid">
            {bedOptions
              .filter(bed => bed.roomId === 'A' && bed.position === roomToggles.A)
              .map(bed => (
                <div 
                  key={bed.id}
                  className={`bed-item ${bed.available ? 'available' : 'unavailable'} ${isBedSelected(bed.id) ? 'selected' : ''} ${bed.position}`}
                  onClick={() => toggleBedSelection(bed.id)}
                  title={undefined}
                >
                  <div className="bed-icon">
                    {getBedTypeIcon(bed.type)}
                  </div>
                  <div className="bed-number">{bed.display}</div>
                  <div className="bed-position">{bed.position}</div>
                  {!bed.available && <div className="bed-unavailable-overlay" style={{ pointerEvents: 'none' }}>Unavailable</div>}
                </div>
              ))
            }
          </div>
        </div>
        
        {/* Room B Section */}
        <div className="dormitory-section">
          <h3>{getBedTypeLabel('B')}</h3>
          <div className="room-info">
            <span className="bed-count">16 beds total</span>
            <span className="bed-type">Female/Family Dormitory</span>
          </div>
          {/* Toggle for Room B */}
          <div className="bed-toggle-group">
            <button
              className={`bed-toggle-btn${roomToggles.B === 'lower' ? ' active' : ''}`}
              onClick={() => handleToggle('B', 'lower')}
            >
              Lower Beds
            </button>
            <button
              className={`bed-toggle-btn${roomToggles.B === 'upper' ? ' active' : ''}`}
              onClick={() => handleToggle('B', 'upper')}
            >
              Upper Beds
            </button>
          </div>
          <div className="bed-grid">
            {bedOptions
              .filter(bed => bed.roomId === 'B' && bed.position === roomToggles.B)
              .map(bed => (
                <div 
                  key={bed.id}
                  className={`bed-item ${bed.available ? 'available' : 'unavailable'} ${isBedSelected(bed.id) ? 'selected' : ''} ${bed.position}`}
                  onClick={() => toggleBedSelection(bed.id)}
                  title={undefined}
                >
                  <div className="bed-icon">
                    {getBedTypeIcon(bed.type)}
                  </div>
                  <div className="bed-number">{bed.display}</div>
                  <div className="bed-position">{bed.position}</div>
                  {!bed.available && <div className="bed-unavailable-overlay" style={{ pointerEvents: 'none' }}>Unavailable</div>}
                </div>
              ))
            }
          </div>
        </div>
      </div>
      
      <div className="dormitory-footer">
        <div className="selected-beds-info">
          Selected: {selectedBeds.length}/{bedsToSelect} beds
        </div>
        <div className="dormitory-actions">
          <button className="btn" onClick={selectAllBeds} style={{ marginRight: 8 }}>
            Select All Beds
          </button>
          <button className="btn btn-secondary" onClick={onCancel}>
            Back
          </button>
          <button 
            className="btn btn-primary" 
            onClick={handleConfirm}
            disabled={selectedBeds.length !== bedsToSelect}
          >
            Continue to Booking
          </button>
        </div>
      </div>
    </div>
  );
};

export default DormitoryLayout;
